import React from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import styled, { withTheme } from 'styled-components';
import hooks from '@g4/automation-hooks';

import { Box, VisuallyHidden } from '../../Boneyard';

const IconChecked = withTheme(({ theme }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g fill="none" fillRule="evenodd">
      <rect
        width="19"
        height="19"
        x=".5"
        y=".5"
        fill={theme.colors.checkbox.backgroundActive}
        stroke={theme.colors.checkbox.outline}
        strokeWidth="2"
        rx="4"
        transform="translate(2 2)"
      />
      <path
        fill={theme.colors.checkbox.checkmarkActive}
        d="M8 15.42l-5-5 1.41-1.41L8 12.59 15.59 5 17 6.42z"
        transform="translate(2 2)"
      />
    </g>
  </svg>
));

const IconUnchecked = withTheme(({ theme }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g fill="none" fillRule="evenodd">
      <rect
        width="18"
        height="18"
        x="1"
        y="1"
        fill={theme.colors.checkbox.background}
        fillRule="evenodd"
        stroke={theme.colors.checkbox.outline}
        strokeWidth="2"
        rx="4"
        transform="translate(2 2)"
      />
    </g>
  </svg>
));

const IconCheckedDisabled = withTheme(({ theme }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g fill="none" fillRule="evenodd" transform="translate(2 2)">
      <rect
        width="19"
        height="19"
        x=".5"
        y=".5"
        fill={theme.colors.checkbox.backgroundDisabled}
        stroke={theme.colors.checkbox.outlineDisabled}
        rx="4"
      />
      <path
        fill={theme.colors.checkbox.checkmarkDisabled}
        d="M8 15.42l-5-5 1.41-1.41L8 12.59 15.59 5 17 6.42z"
      />
    </g>
  </svg>
));

const IconUncheckedDisabled = withTheme(({ theme }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <rect
      width="18"
      height="18"
      x="1"
      y="1"
      fill={theme.colors.checkbox.backgroundDisabled}
      fillRule="evenodd"
      stroke={theme.colors.checkbox.outlineDisabled}
      strokeWidth="2"
      rx="4"
      transform="translate(2 2)"
    />
  </svg>
));

const IconUncheckedError = withTheme(({ theme }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <g fill="none" fillRule="evenodd">
      <rect
        width="18"
        height="18"
        x="1"
        y="1"
        fill={theme.colors.checkbox.background}
        fillRule="evenodd"
        stroke={theme.colors.checkbox.checkmarkError}
        strokeWidth="2"
        rx="4"
        transform="translate(2 2)"
      />
    </g>
  </svg>
));

const CheckboxWrapper = styled.div`
  height: ${props => (props.isNested ? '16px' : '24px')};
  border-radius: 6px;
  align-self: ${props => props.iconAlignment};
  ${props => (props.focused ? props.theme.focusStyle : null)}
`;

export const LAYOUT_INLINE_ICON_TOP = 'inline-icon-top';
export const LAYOUT_INLINE_ICON_CENTER = 'inline-icon-center';
export const LAYOUT_STACKED_ICON_LEFT = 'stacked-icon-left';
export const LAYOUT_STACKED_ICON_CENTER = 'stacked-icon-center';

export const propTypes = {
  /** HTML `name` attribute value */
  name: PropTypes.string.isRequired,
  /** Determines if the checkbox is checked */
  value: PropTypes.bool,
  /** Prevents users from modifying the checked state */
  disabled: PropTypes.bool,
  /**
   * IDs of the the elements that describe the field.
   * Useful when referencing an associated tooltip.
   */
  'aria-describedby': PropTypes.string,
  /** Visual layout */
  layout: PropTypes.oneOf([
    LAYOUT_INLINE_ICON_TOP,
    LAYOUT_INLINE_ICON_CENTER,
    LAYOUT_STACKED_ICON_LEFT,
    LAYOUT_STACKED_ICON_CENTER,
  ]),
  /** Active icon */
  iconChecked: PropTypes.element,
  /** Inactive icon */
  iconUnchecked: PropTypes.element,
  /** Disabled active icon */
  iconCheckedDisabled: PropTypes.element,
  /** Disabled inactive icon */
  iconUncheckedDisabled: PropTypes.element,
  /** Disabled error icon */
  iconUncheckedError: PropTypes.element,
  /** Associates a specific action onto an element for the benefit of automated clients consuming it */
  hasError: PropTypes.bool,
  errorId: PropTypes.string,
  automationHook: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
  isNested: PropTypes.bool,
  iconAlignment: PropTypes.string,
};

export const defaultProps = {
  value: false,
  layout: LAYOUT_INLINE_ICON_TOP,
  iconChecked: <IconChecked />,
  iconUnchecked: <IconUnchecked />,
  iconCheckedDisabled: <IconCheckedDisabled />,
  iconUncheckedDisabled: <IconUncheckedDisabled />,
  iconUncheckedError: <IconUncheckedError />,
  onChange: Function.prototype,
  onBlur: Function.prototype,
  onFocus: Function.prototype,
  isNested: false,
  iconAlignment: 'center',
  hasError: false,
};

// prettier-ignore
export const Checkbox = styled(props => {
  const onChange = debounce(props.onChange, 150, {
    leading: true,
    trailing: false,
  });

const [isFocused, setIsFocused] = React.useState(false);
return (
  <label
    className={props.className}
    {...hooks.hook(props.automationHook, 'label')}
  >
    <VisuallyHidden>
      <input
        aria-invalid={props.hasError}
        type="checkbox"
        name={props.name}
        value={props.value}
        disabled={props.disabled}
        aria-describedby={props.hasError ? props.errorId : undefined}
        {...hooks.hook(props.automationHook)}
        onChange={e => {
          /* need this because without it, the onBlur event fires separately from onFocus+onChange when trying to
          check the same checkbox => change only happens every second click on the same checkbox.
          TO DO: revisit this issue. Focus styles should stay in place */
          setIsFocused(false);

          onChange(e.target.value !== 'true');
        }}
        onBlur={() => setIsFocused(false)}
        onFocus={() => setIsFocused(true)}
        /* removed onKeyDown because it was causing issues with keyboard value changes */
      />
    </VisuallyHidden>

    <CheckboxWrapper
      focused={isFocused}
      isNested={props.isNested}
      iconAlignment={props.iconAlignment}
    >
      {props.disabled
        ? props.value
          ? props.iconCheckedDisabled
          : props.iconUncheckedDisabled
        : props.value
        ? props.iconChecked
        : props.hasError ? props.iconUncheckedError : props.iconUnchecked}
    </CheckboxWrapper>

    <Box
      ml={
        [LAYOUT_INLINE_ICON_TOP, LAYOUT_INLINE_ICON_CENTER].includes(
          props.layout
        )
          ? 2
          : undefined
      }
      children={props.children}
    />
  </label>
);
})`
  display: inline-flex;

  ${props => props.layout === LAYOUT_INLINE_ICON_CENTER && `
    align-items: center;
  `}

  ${props => props.layout === LAYOUT_STACKED_ICON_LEFT && `
    flex-direction: column;
  `}

  ${props => props.layout === LAYOUT_STACKED_ICON_CENTER && `
    flex-direction: column;
    align-items: center;
  `}
`;

Checkbox.propTypes = propTypes;
Checkbox.defaultProps = defaultProps;

/** @component */
export default Checkbox;
